
import Vue from 'vue';
import BravadoTypeformSteps from '~/components/common/BravadoTypeform/BravadoTypeformSteps.vue';
import BravadoTypeform from '~/components/common/BravadoTypeform/BravadoTypeform.vue';
import type { IStep } from '~/components/partials/public-portfolio/PublicPortfolioTestify/constants';
import {
  flow,
  REVIEW_ROUTE_NAME,
  ANALYTICS_STEPS_NAMES,
  stepNames,
  steps,
} from '~/components/partials/public-portfolio/PublicPortfolioTestify/constants';
import JobsNextButton from '~/components/partials/jobs/JobsNextButton.vue';
import PublicPortfolioTestifyInfo from '~/components/partials/public-portfolio/PublicPortfolioTestify/PublicPortfolioTestifyInfo.vue';
import PublicPortfolioTestifyCompliments from '~/components/partials/public-portfolio/PublicPortfolioTestify/PublicPortfolioTestifyCompliments.vue';
import PublicPortfolioTestifyWords from '~/components/partials/public-portfolio/PublicPortfolioTestify/PublicPortfolioTestifyWords.vue';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';
// @ts-ignore
import EndorseTestimonialForPublicPortfolioUser from '~/apollo/mutations/Portfolio/EndorseTestimonialForPublicPortfolioUser.mutations.gql';
import { TestimonialQuery } from '~/apollo/queries/Testimonial.query';
import StartTestimonialEndorsingForPublicPortfolioUser from '~/apollo/mutations/Portfolio/StartTestimonialEndorsingForPublicPortfolioUser.mutations';
import type { PublicPortfolioUser, Testimonial } from '~/types/graphql';
import { TestimonialState } from '~/types/graphql';
import { testimonialStates } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/constants';
import PublicPortfolioUserVisibilityQuery from '~/apollo/queries/PublicPortfolio/PublicPortfolioUserVisibility.query';

interface IData {
  publicPortfolioUser: PublicPortfolioUser | null;
  testimonialSign: string | null;
  formState: string;
  testimonial: Testimonial;
}

export default Vue.extend({
  name: 'TestifyPage',
  components: {
    JobsNextButton,
    BravadoText,
    BravadoTypeform,
    BravadoTypeformSteps,
    PublicPortfolioTestifyInfo,
    PublicPortfolioTestifyCompliments,
    PublicPortfolioTestifyWords,
  },
  layout: 'clear',
  middleware({ redirect, route }) {
    const {
      query: { testifySign },
    } = route;

    if (!testifySign) {
      redirect('/home');
    }
  },
  data(): IData {
    return {
      publicPortfolioUser: null,
      testimonialSign: null,
      formState: 'idle',
      testimonial: {
        id: '',
        author: { firstname: '', lastname: '' },
        skills: [],
        description: '',
        sign: '',
        state: TestimonialState.New,
        position: -1,
        updatedAt: '',
        requestor: {
          slug: '',
        },
      },
    };
  },
  apollo: {
    testimonial: {
      query: TestimonialQuery,
      variables() {
        const sign = this.testimonialSign as string;
        return { sign };
      },
      skip(): boolean {
        return !this.testimonialSign;
      },
      async result({ data }: { data: { testimonial: Testimonial } }) {
        if (!data) return;
        const { testimonial } = data;
        const { step } = this.$route.params;

        if (!testimonialStates.REVIEW_TESTIMONIAL_STATES.includes(testimonial.state)) {
          if (stepNames.THANKS === step) localStorage.removeItem(this.localStorageKey);
          await this.goToStep(stepNames.THANKS);
          return;
        }

        if (!testimonial.author.firstname && step !== stepNames.INFO) {
          this.goToStep(stepNames.INFO);
          return;
        }

        if (!testimonial.skills.length && (step === stepNames.WORDS || step === stepNames.THANKS)) {
          this.goToStep(stepNames.COMPLIMENTS);
          return;
        }

        if (!testimonial.description && step === stepNames.THANKS) {
          this.goToStep(stepNames.WORDS);
        }
      },
      prefetch: false,
    },
    publicPortfolioUser: {
      // To check if user's portfolio is public
      query: PublicPortfolioUserVisibilityQuery,
      variables() {
        const slug = this.userSlug as string;
        return { slug };
      },
      skip(): boolean {
        return !(this.userSlug && this.isLastStep);
      },
      prefetch: false,
    },
  },
  computed: {
    step(): IStep {
      return this.steps[this.stepName];
    },
    stepName(): string {
      return this.$route.params.step;
    },
    userName(): string {
      const user = this.testimonial.requestor;
      return `${user?.firstname || ''} ${user?.lastname || ''}`;
    },
    userSlug(): string {
      return this.testimonial?.requestor?.slug || '';
    },
    steps(): { [key: string]: IStep } {
      return steps(this.userName);
    },
    image(): () => string {
      return () => require('~/components/partials/public-portfolio/PublicPortfolioTestify/assets/thumbs-up.vue.svg');
    },
    isLastStep(): boolean {
      return this.stepName === stepNames.THANKS;
    },
    isInfoStep(): boolean {
      return this.stepName === stepNames.INFO;
    },
    isWordsStep(): boolean {
      return this.stepName === stepNames.WORDS;
    },
    stepIndex(): number {
      return flow.indexOf(this.stepName);
    },
    stepAmount(): number {
      return flow.length - 1;
    },
    testifySign(): string {
      return (this.$route.query.testifySign as string) || '';
    },
    localStorageKey(): string {
      return `testify-sign-${this.testifySign}`;
    },
    isPortfolioButtonVisible(): boolean {
      return this.isLastStep && !!this.publicPortfolioUser;
    },
  },
  mounted() {
    if (this.stepName === stepNames.INFO) {
      this.$analytics.logEvent('public-portfolio__give-testimonial', { to: ANALYTICS_STEPS_NAMES[stepNames.INFO] });
    }

    this.testimonialSign = localStorage.getItem(this.localStorageKey);
    if (!this.testimonialSign) {
      this.startNewReview();
    }
  },
  methods: {
    async handleBack(): Promise<void> {
      if (!this.isInfoStep) {
        const nextStep = flow[this.stepIndex - 1];
        await this.goToStep(nextStep);
      }
    },
    async handleNext(data: Partial<Testimonial>): Promise<void> {
      if (!this.isLastStep && this.formState !== 'submitting') {
        try {
          this.formState = 'submitting';
          const {
            skills,
            sign,
            description,
            author: { firstname, lastname },
          } = { ...this.testimonial, ...data };
          await this.$apollo.mutate({
            mutation: EndorseTestimonialForPublicPortfolioUser,
            variables: {
              input: { skills, sign, description, author: { firstname, lastname } },
            },
            update: (store, { data: { endorseTestimonialForPublicPortfolioUser } }) => {
              const query = {
                query: TestimonialQuery,
                variables: {
                  sign: this.testimonialSign,
                },
              };
              const data = store.readQuery<{ testimonial: Testimonial }>(query);

              if (!data) return;

              data.testimonial = { ...(data.testimonial || {}), ...endorseTestimonialForPublicPortfolioUser };
              store.writeQuery({ ...query, data });
            },
          });
          const nextStep = flow[this.stepIndex + 1];
          this.$analytics.logEvent('public-portfolio__give-testimonial', { to: ANALYTICS_STEPS_NAMES[nextStep] });
          await this.goToStep(nextStep);
        } catch (e) {
          this.$notice.error(e);
          this.$sentryUtils.error(e);
        } finally {
          this.formState = 'idle';
        }
      }
    },
    async goToStep(step: string): Promise<void> {
      if (this.stepName === step) return;
      const {
        query: { testifySign },
      } = this.$route;

      await this.$router.push({
        name: REVIEW_ROUTE_NAME,
        params: { step },
        query: { testifySign },
      });
    },
    async handleClick(): Promise<void> {
      await this.$router.push(`/p/${this.userSlug}`);
    },
    async startNewReview(): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: StartTestimonialEndorsingForPublicPortfolioUser,
          variables: {
            input: {
              testifySign: this.testifySign,
            },
          },
          update: (store, { data: { startTestimonialEndorsingForPublicPortfolioUser } }) => {
            this.testimonialSign = startTestimonialEndorsingForPublicPortfolioUser.sign;
            localStorage.setItem(this.localStorageKey, this.testimonialSign as string);
          },
        });
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      }
    },
  },
});
